import 'lite-youtube-embed'
import { createApp } from 'vue'
import Cookies from 'js-cookie'
import { createPinia } from 'pinia'
import 'virtual:svg-icons-register'
import '@css/main.css'
import router from './router'
import { segment } from '@js/utils/segment'

import components from '@js/components'
import { setEnvSingleValue, setEnvTopLevelKey } from '@js/utils/env'

import { Affirm } from '@js/types/Affirm'
import { AcceptDispatchData, AuthorizeDispatchResponse } from '@js/types/AuthorizeNet'

declare global {
  interface Window {
    grecaptcha?: {
      ready?: (callback: () => void) => void
      execute?: (siteKey: string, options: { action: string }) => Promise<string>
    }
    WCEI_CONFIG: string
    WCEI: {
      CONFIG: {
        DEBUG: boolean
        BASE_URL: string
        RECAPTCHA: {
          SITE_KEY: string
          SECRET_KEY: string
        }
        MAGENTO: {
          STORE_NAME: string
          GRAPHQL_URL: string
        },
        ELASTICSEARCH: {
          PROXY_URL: string
          SEARCH_INDEX: string
        },
        AUTHORIZE_NET: {
          API_LOGIN_ID: string
          CLIENT_KEY: string
        },
        AFFIRM: {
          PUBLIC_KEY: string
          SCRIPT_URL: string
        },
        RLP: {
          SSO_URL: string
        },
        SEGMENT: {
          API_KEY: string
          SERVER_API_KEY: string
        }
      },
    }
    Accept: {
      dispatchData: (
        data: AcceptDispatchData,
        callback: (response: AuthorizeDispatchResponse) => Promise<void>,
      ) => void
    }
    affirm: Affirm
  }
}

// Ensure images go in the manifest, this doesn't increase the bundle size
import.meta.glob('@img/**/*', { query: '?url', import: 'default', eager: true })

// Decode WCEI data from Base64
try {
  window.WCEI = JSON.parse(atob(window.WCEI_CONFIG))
} catch (error) {
  console.error('Failed to decode WCEI configuration:', error)
}

// Set base URL for the app
setEnvSingleValue('BASE_URL', window.WCEI.CONFIG.BASE_URL)

// Set Google reCAPTCHA environment variables
setEnvTopLevelKey('recaptcha', window.WCEI.CONFIG.RECAPTCHA)

// Set Magento environment variables
setEnvTopLevelKey('magento', window.WCEI.CONFIG.MAGENTO)

// Set Elasticsearch environment variables
setEnvTopLevelKey('elasticsearch', window.WCEI.CONFIG.ELASTICSEARCH)

// Set Authorize.Net environment variables
setEnvTopLevelKey('authorizeNet', window.WCEI.CONFIG.AUTHORIZE_NET)

// Set Affirm environment variables
setEnvTopLevelKey('affirm', window.WCEI.CONFIG.AFFIRM)

// Set RLP environment variables
setEnvTopLevelKey('rlp', window.WCEI.CONFIG.RLP)

// Set Segment environment variables
setEnvTopLevelKey('segment', window.WCEI.CONFIG.SEGMENT)

// This is before app creation to ensure all components have proper access to store name
Cookies.set('storeName', window.WCEI.CONFIG.MAGENTO.STORE_NAME ?? 'default')

const pinia = createPinia()

const app = createApp({})

components.forEach((Component) => {
  app.component(Component.name ?? '', Component)
})

app.use(pinia)
app.use(router)
app.use(segment)

app.mount('#app')

// Accept HMR as per: https://vitejs.dev/guide/api-hmr.html to prevent full page reload on changes
if (import.meta.hot) {
  import.meta.hot.accept(() => {
    console.log('HMR')
  })
}
